import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Aurora from "@/components/Aurora";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { PrivacyLink } from "@/components/PrivacyModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_NUMBER = "558594032594";
const WA_LINK = `https://wa.me/${WA_NUMBER}`;

const SEGMENTOS = ["Clínica médica", "Estética & beleza", "Odontologia", "Petshop / Veterinária", "Outro negócio de atendimento"];
const VOLUMES = ["Até 100 atendimentos/mês", "100 a 300 atendimentos/mês", "300 a 800 atendimentos/mês", "Mais de 800 atendimentos/mês"];

const inputCls =
  "w-full rounded-2xl border border-white/15 bg-white/[0.06] px-5 py-4 text-sm text-white placeholder-white/40 outline-none backdrop-blur-sm transition-[border-color,background-color] duration-300 focus:border-turquoise focus:bg-white/[0.09]";

const Field = ({ label, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-2 block font-mono2 text-[10px] uppercase tracking-[0.25em] text-sage/80">
      {label}
    </label>
    {children}
  </div>
);

export default function DemoForm() {
  const [form, setForm] = useState({ nome: "", negocio: "", segmento: SEGMENTOS[0], whatsapp: "", volume: VOLUMES[0] });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    try {
      // Estrutura pronta para integração com CRM/e-mail: o lead é persistido
      // via API e pode ser encaminhado a qualquer destino adicional no backend.
      await axios.post(`${API}/leads`, form);
      setStatus("success");
      toast.success("Recebido! Abrindo o WhatsApp para você falar com a gente.");
      const text = encodeURIComponent(
        `Olá! Sou ${form.nome}, do negócio ${form.negocio} (${form.segmento}). Quero agendar uma demonstração gratuita da CliniAtiva.`
      );
      window.open(`${WA_LINK}?text=${text}`, "_blank");
      setForm({ nome: "", negocio: "", segmento: SEGMENTOS[0], whatsapp: "", volume: VOLUMES[0] });
    } catch (err) {
      setStatus("error");
      toast.error("Não conseguimos registrar agora. Chame a gente direto no WhatsApp.");
    }
  };

  return (
    <section id="demonstracao" data-testid="demo-form-section" className="grain relative overflow-hidden bg-petrol py-28 text-white sm:py-36">
      <Aurora className="absolute inset-0 h-full w-full opacity-70" />
      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-16 px-5 lg:grid-cols-2 lg:px-10">
        <div>
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="font-mono2 text-xs tracking-[0.35em] text-turquoise">12</span>
              <span className="h-px w-12 bg-turquoise" />
              <span className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-sage">Demonstração gratuita</span>
            </div>
          </Reveal>
          <MaskedLines
            lines={["Sua recepção não", "precisa fazer", "tudo sozinha."]}
            delay={0.1}
            className="mt-6 font-display text-3xl font-bold leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl"
          />
          <Reveal delay={0.25}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-sage/85 sm:text-lg">
              Descubra como a CliniAtiva pode funcionar no seu negócio. Preencha ao lado e continue
              a conversa direto no nosso WhatsApp — respondido, é claro, por nós.
            </p>
            <ul className="mt-10 space-y-4">
              {["Demonstração com a realidade do seu negócio", "Sem compromisso e sem cartão", "Seus dados ficam registrados com segurança"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm text-sage sm:text-base">
                  <span className="h-1.5 w-1.5 rounded-full bg-turquoise" />
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <motion.form
            data-testid="demo-form"
            onSubmit={submit}
            className="glass-dark rounded-[28px] p-8 shadow-[0_40px_80px_-30px_rgba(4,20,28,0.7)] sm:p-10"
          >
            <div className="space-y-6">
              <Field label="Seu nome" htmlFor="demo-nome">
                <input id="demo-nome" data-testid="demo-input-nome" required minLength={2} value={form.nome} onChange={set("nome")} placeholder="Como podemos te chamar?" className={inputCls} />
              </Field>
              <Field label="Nome do negócio" htmlFor="demo-negocio">
                <input id="demo-negocio" data-testid="demo-input-negocio" required minLength={2} value={form.negocio} onChange={set("negocio")} placeholder="Ex.: Clínica Vida Plena" className={inputCls} />
              </Field>
              <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Segmento" htmlFor="demo-segmento">
                  <select id="demo-segmento" data-testid="demo-select-segmento" value={form.segmento} onChange={set("segmento")} className={`${inputCls} appearance-none`}>
                    {SEGMENTOS.map((s) => (
                      <option key={s} value={s} className="bg-petrol text-white">{s}</option>
                    ))}
                  </select>
                </Field>
                <Field label="WhatsApp" htmlFor="demo-whatsapp">
                  <input id="demo-whatsapp" data-testid="demo-input-whatsapp" required minLength={8} value={form.whatsapp} onChange={set("whatsapp")} placeholder="(85) 99999-0000" className={inputCls} />
                </Field>
              </div>
              <Field label="Volume mensal de atendimentos" htmlFor="demo-volume">
                <select id="demo-volume" data-testid="demo-select-volume" value={form.volume} onChange={set("volume")} className={`${inputCls} appearance-none`}>
                  {VOLUMES.map((v) => (
                    <option key={v} value={v} className="bg-petrol text-white">{v}</option>
                  ))}
                </select>
              </Field>
            </div>

            {status === "success" && (
              <motion.p
                data-testid="demo-success-message"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 flex items-start gap-2.5 rounded-2xl border border-turquoise/40 bg-turquoise/10 p-4 text-sm text-turquoise"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                Dados registrados com sucesso! Abrimos o WhatsApp para você continuar a conversa. Se a aba não abriu,{" "}
                <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                  toque aqui
                </a>
                .
              </motion.p>
            )}
            {status === "error" && (
              <motion.p
                data-testid="demo-error-message"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 flex items-start gap-2.5 rounded-2xl border border-red-400/40 bg-red-400/10 p-4 text-sm text-red-300"
              >
                <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
                Não conseguimos registrar seus dados agora. Tente novamente ou fale direto no{" "}
                <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                  WhatsApp +55 85 9403-2594
                </a>
                .
              </motion.p>
            )}

            <motion.button
              data-testid="demo-submit-button"
              type="submit"
              disabled={status === "loading"}
              whileHover={{ scale: status === "loading" ? 1 : 1.02 }}
              whileTap={{ scale: status === "loading" ? 1 : 0.98 }}
              className="group mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-turquoise py-4 font-display text-base font-bold text-petrol transition-shadow duration-300 hover:shadow-[0_16px_40px_rgba(39,179,176,0.45)] disabled:opacity-70"
            >
              {status === "loading" ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" /> Registrando…
                </>
              ) : (
                <>
                  Agendar demonstração gratuita
                  <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </>
              )}
            </motion.button>
            <p className="mt-4 text-center text-xs leading-relaxed text-white/40">
              Ao enviar, você continua a conversa no WhatsApp +55 85 9403-2594. Seus dados são usados
              apenas para o contato sobre a demonstração, conforme nossa{" "}
              <PrivacyLink className="underline decoration-turquoise underline-offset-2 transition-colors duration-300 hover:text-turquoise" />
              .
            </p>
          </motion.form>
        </Reveal>
      </div>
    </section>
  );
}
